const VIDEO_FILE_PATTERN = /\.(?:mp4|mov|m4v|webm|avi|mkv)(?:$|[?#])/i;

export const isVideoResource = (resource) => (
  resource?.typeCode?.toLowerCase().includes('video')
  || resource?.category?.toLowerCase() === 'video'
);

export const getResourceThumbnailUrl = (resource) => {
  if (!resource) return null;

  if (!isVideoResource(resource)) {
    return resource.thumbnailUrl || resource.url || null;
  }

  const thumbnailUrl = resource.thumbnailUrl?.trim();
  if (!thumbnailUrl || thumbnailUrl === resource.url || VIDEO_FILE_PATTERN.test(thumbnailUrl)) {
    return null;
  }
  return thumbnailUrl;
};

export const formatMediaDuration = (duration) => {
  const totalSeconds = Math.max(0, Math.round(Number(duration)));
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getResourceTimestamp = (resource) => {
  if (!resource?.createdAt) return null;
  const timestamp = new Date(resource.createdAt).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const sortResourcesByDateDesc = (resources) => (
  (resources || [])
    .map((resource, index) => ({ resource, index, timestamp: getResourceTimestamp(resource) }))
    .sort((left, right) => {
      if (left.timestamp == null && right.timestamp == null) return left.index - right.index;
      if (left.timestamp == null) return 1;
      if (right.timestamp == null) return -1;
      return right.timestamp - left.timestamp || left.index - right.index;
    })
    .map(({ resource }) => resource)
);

const getLocalCalendarDay = (date) => Date.UTC(
  date.getFullYear(),
  date.getMonth(),
  date.getDate()
);

const getDateGroup = (resource, now) => {
  const timestamp = getResourceTimestamp(resource);
  if (timestamp == null) {
    return { key: 'undated', label: '较早资源' };
  }

  const date = new Date(timestamp);
  const dayDifference = Math.round(
    (getLocalCalendarDay(now) - getLocalCalendarDay(date)) / 86_400_000
  );

  if (dayDifference === 0) return { key: 'today', label: '今天' };
  if (dayDifference === 1) return { key: 'yesterday', label: '昨天' };
  if (dayDifference >= 2 && dayDifference <= 6) {
    return { key: `days-${dayDifference}`, label: `${dayDifference}天前` };
  }

  const sameYear = date.getFullYear() === now.getFullYear();
  return {
    key: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
    label: sameYear
      ? `${date.getMonth() + 1}月${date.getDate()}日`
      : `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  };
};

export const groupResourcesByDate = (resources, now = new Date()) => {
  const groups = [];
  const groupMap = new Map();

  (resources || []).forEach((resource) => {
    const dateGroup = getDateGroup(resource, now);
    let group = groupMap.get(dateGroup.key);
    if (!group) {
      group = { ...dateGroup, resources: [] };
      groupMap.set(dateGroup.key, group);
      groups.push(group);
    }
    group.resources.push(resource);
  });

  return groups;
};
